import React, { useEffect, useState } from "react";
import useNaboraHook from "./hooks/useNaboraHook";

const NaboraHero = ({ slug }) => {
  const { getFeaturedExhibition, getExhibitionBySlug } = useNaboraHook();

  const [hero, setHero] = useState(null);

  useEffect(() => {
    loadHero();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadHero = async () => {
    try {
      const data = slug
        ? await getExhibitionBySlug(slug)
        : await getFeaturedExhibition();

      setHero(data || null);
    } catch (err) {
      console.error(err);
    }
  };

  const isFoundHer = slug === "foundher" || hero?.slug === "foundher";

  const heroBadge = hero?.heroBadge || "NABORA";

  const heroSubtitle =
    hero?.heroSubtitle || "Neighborhood & Area Businesses Online — Reach All";

  const heroTitle =
    hero?.heroTitle ||
    (isFoundHer ? "Her Exhibition" : "Neighborhood Exhibition");

  const heroDescription =
    hero?.shortDescription ||
    (isFoundHer
      ? "Her Dreams Goes Phygytal: Collaborate Online Before Doing Offline"
      : "Local Dreams Goes Phygytal: Collaborate Online Before Doing Offline");

  const primaryCTA = hero?.primaryCTA || "Explore Exhibitions";

  const secondaryCTA =
    hero?.secondaryCTA || `Become ${isFoundHer ? "a Woman" : "an"} Exhibitor`;

  return (
    <div className="relative min-h-[100dvh] flex flex-col items-center justify-center bg-gradient-to-br from-violet-950 via-purple-950 to-slate-950 overflow-hidden px-4">
      {/* Background */}

      <div className="absolute inset-0 bg-[radial-gradient(at_50%_30%,rgba(139,92,246,0.15),transparent)]"></div>

      <div className="text-center z-10 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-6 py-2 rounded-full mb-4 border border-white/20">
          <span className="text-yellow-300 text-xl">📍</span>

          <span className="font-medium tracking-widest uppercase text-sm">
            {heroBadge}
          </span>
        </div>

        <p className="uppercase text-lg lg:text-xl text-cyan-200">
          {heroSubtitle}
        </p>

        <h1
          className={`text-xl lg:text-3xl font-bold tracking-tighter mb-4 bg-clip-text text-transparent ${
            isFoundHer
              ? "bg-gradient-to-r from-pink-200 via-fuchsia-300 to-rose-400"
              : "bg-gradient-to-r from-white via-purple-200 to-cyan-200"
          }`}
        >
          {heroTitle}
        </h1>

        <p className="text-lg lg:text-xl text-purple-200 mb-4">
          {heroDescription}
        </p>

        <div className="flex flex-col lg:flex-row gap-2 lg:gap-6 justify-center mt-2">
          <button className="w-full px-2 py-2 bg-gradient-to-r from-fuchsia-500 to-violet-600 rounded-xl font-semibold text-xs lg:text-sm flex items-center justify-center gap-2 whitespace-nowrap hover:scale-105 transition-all active:scale-95 shadow-xl shadow-purple-500/50">
            <span>✨ {primaryCTA}</span>

            <span className="text-xl shrink-0">→</span>
          </button>

          <button className="w-full px-2 py-2 border-2 border-white/30 hover:border-white/70 rounded-xl font-semibold text-xs lg:text-sm backdrop-blur-md transition-all whitespace-nowrap">
            {secondaryCTA} →
          </button>
        </div>
      </div>

      {/* Floating elements */}

      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-8 text-2xl lg:text-3xl opacity-70">
        <div className="animate-bounce">🏪</div>

        <div className="animate-bounce delay-150">🛍️</div>

        <div className="animate-bounce delay-300">🎉</div>
      </div>

      {/* Quarter & Year */}

      {hero && (hero.quarter || hero.year) && (
        <div className="absolute top-6 right-4 lg:right-8 z-20">
          <div className="rounded-2xl border border-cyan-400/20 bg-black/30 backdrop-blur-xl px-4 py-2">
            <div className="text-[10px] uppercase tracking-[3px] text-cyan-300">
              Current Exhibition
            </div>

            <div className="text-yellow-200 font-bold text-sm lg:text-base">
              {[hero.quarter, hero.year].filter(Boolean).join(" • ")}
            </div>
          </div>
        </div>
      )}

      {/* Hero Image Overlay (Future Ready) */}

      {hero?.heroImage && (
        <div
          className="absolute inset-0 opacity-[0.08] bg-center bg-cover pointer-events-none"
          style={{
            backgroundImage: `url(${hero.heroImage})`,
          }}
        />
      )}
    </div>
  );
};

export default NaboraHero;
